<template>
  <div v-if="canSearch">
    <div class="card" id="my-tasks">
      <form class="form-horizontal">
        <div class="card-header my-tasks-header">
          <div class="my-tasks-filter pull-right">
            <button @click.prevent="fetchByAssigneeAndStatusAndNotify" class="m-right-12 btn btn-primary btn-fill">Refresh</button>
            <p-checkbox v-model="autoRefresh" style="display:inline; margin-right: 12px;">
              auto-refresh
            </p-checkbox>
            <el-select class="select task-statuses"
                       multiple
                       size="large"
                       style="width: 450px"
                       @change="filterByStatus"
                       placeholder="Task Status"
                       clearable
                       v-model="selectedStatus">
              <el-option v-for="status in statuses"
                         class="select"
                         :value="status.value"
                         :label="status.label"
                         :key="status.label">
              </el-option>
            </el-select>
            <el-select name="taskName"
                       clearable
                       size="large"
                       placeholder="Task Name"
                       @change="filterByTaskNameId"
                       style="padding-left: 12px;"
                       v-model="taskNameId">
              <el-option v-for="option in uniqueTaskNames"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <el-date-picker
              name="dueDate"
              type="date"
              placeholder="Due Date"
              @input="filterByDueDate"
              :editable=false
              v-model="dueDate"
              :format="constants.DATE.FORMAT.DISPLAY"
              :picker-options="datePickerConfig"
              style="margin-left: 12px; width: 150px">
            </el-date-picker>
            <a @click.prevent="printerFriendly" v-if="hasResults" href="#"><img src="static/img/icon-print.png" title="Print My Tasks" class="m-left-12"/></a>
            <a @click.prevent="downloadExcel" v-if="hasResults" href="#"><img src="static/img/icon-excel.jpg" title="Export My Tasks to Excel" class="m-left-12"/></a>
            <a href="#" @click.prevent="handleShowPreferenceModal"><img style="margin-left: 12px; width: 26px; height: 26px;" src="static/img/gear-icon.png" title="Configure Columns Displayed" /></a>
          </div>
        </div>
        <div class="card-body">
          <el-table id="my-tasks-table" class="table-striped wrap-whole-words search-tasks-table indexed-table-column-width"
                    :data="sortedTableData"
                    :key="tableKey"
                    row-class-name="draggable cursor-pointer"
                    :row-style="isOrphanedTask"
                    :header-cell-class-name="headerSortClass"
                    @selection-change="handleSelectionChange"
                    border
                    :max-height="tableHeight(275)"
                    style="width:100%">
            <el-table-column
                :width="this.constants.INDEX_COLUMN_WIDTHS.WIDE"
                align="center"
                label="#"
                type="index" />
            <el-table-column
              label="Select"
              align="center"
              :selectable="selectableTask"
              type="selection"
              width="60">
            </el-table-column>
            <el-table-column v-for="column in filteredTableColumns"
                             :min-width="column.minWidth"
                             :key="column.label"
                             :label="column.label">
              <template v-slot="props">
                <component :is="column.component" :data="getColumnData(column, props.row)" />
              </template>
            </el-table-column>
            <el-table-column
              width="170"
              class="el-table__fixed"
              fixed="right"
              label="Actions">
              <template v-slot="props">
                <p-button type="success" size="md" icon class="btn-left" title="View/Edit Task">
                  <span class="invisible">Task Id: {{ props.row.id }}</span>
                  <i class="fa fa-edit" @click="handleEditTask(props.row)"></i>
                </p-button>
                <p-button v-if="canReadJobs" type="success" size="md" icon class="btn-left" title="View/Edit Job">
                  <span class="invisible">Job Id: {{ props.row.jobId }}</span>
                  <i class="nc-icon nc-ruler-pencil" @click="handleEditJob(props.row.jobId)"></i>
                </p-button>
                <p-button v-if="canReadProjects" type="success" size="md" icon class="btn-left" title="View/Edit Project">
                  <span class="invisible">Project Id: {{ props.row.projectId }}</span>
                  <i class="nc-icon nc-layout-11" @click="handleEditProject(props.row.projectId)"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import {Button as PButton} from '@/components/UIComponents'
  import {
    assembleTableColumnSortProps,
    filterAndSortTableColumns,
    getTableHeight,
    notifySuccess,
    printModal,
    sortNested,
    tableHeaderSortCssClass
  } from '@/utilities/utils'
  import Constants from '@/utilities/constants'
  import Permissions from '@/utilities/permissions'
  import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'
  import lodash from 'lodash'
  import tableColumns from '@/utilities/tableColumns'

  export default {
    components: {
      PButton
    },
    computed: {
      ...mapGetters({
        user: 'security/user',
        taskTemplates: 'tasks/templates/results',
        uniqueTaskNames: 'tasks/myTasks/uniqueTaskNames',
        statuses: 'statuses/ptracTask',
        tableData: 'tasks/myTasks/results'
      }),
      sortedTableData() {
        return this.tableData.sort(sortNested(this.sortedProps))
      },
      filteredTableColumns() {
        return filterAndSortTableColumns(this.tableColumns, this.user, Constants.USER_PREFERENCE_TYPES.MY_TASKS)
      },
      sortedProps() {
        return assembleTableColumnSortProps(this.filteredTableColumns)
      },
      hasResults() {
        return this.tableData.length > 0
      },
      canSearch() {
        return this.$can('read', Permissions.MY_TASKS)
      },
      canReadJobs() {
        return this.$can('read', Permissions.JOBS)
      },
      canReadProjects() {
        return this.$can('read', Permissions.PROJECTS)
      }
    },
    data() {
      return {
        constants: Constants,
        datePickerConfig,
        autoRefresh: true,
        btnDownloadUnlocked: false,
        dueDate: null,
        sortableOptionValuesInitialized: false,
        taskNameId: null,
        timer: null,
        tableKey: 0,
        selectedStatus: [Constants.STATUSES.PENDING],
        tableColumns: tableColumns.MY_TASKS
      }
    },
    methods: {
      headerSortClass({columnIndex}) {
        return tableHeaderSortCssClass(this.filteredTableColumns, columnIndex, 2)
      },
      getColumnData(column, row) {
        return column.prop ? lodash.get(row, column.prop) : row
      },
      printerFriendly() {
        this.$eventHub.$emit('show-modal-1', 'Print My Tasks', null, '90%')
        printModal('printMyTasks')
      },
      downloadExcel() {
        this.$store.dispatch('tasks/myTasks/resultsToExcel')
      },
      selectableTask(task) {
        return !(Constants.STATUSES.COMPLETED === task.status || Constants.STATUSES.CANCELLED === task.status || task.orphaned)
      },
      filterByTaskNameId() {
        this.$store.commit('tasks/myTasks/nameId', this.taskNameId)
        this.fetchByAssigneeAndStatus()
      },
      filterByStatus() {
        this.$store.commit('tasks/myTasks/selectedStatus', this.selectedStatus)
        this.fetchByAssigneeAndStatus()
      },
      filterByDueDate(dueDate) {
        this.$store.commit('tasks/myTasks/filterByDueDate', dueDate)
        this.fetchByAssigneeAndStatus()
      },
      handleSelectionChange(tasks) {
        this.$store.commit('tasks/myTasks/selected', tasks)
      },
      handleEditTask(task) {
        this.$eventHub.$emit('show-modal-2', 'Edit Task', {id: task.id})
      },
      handleEditJob(jobId) {
        this.$eventHub.$emit('show-modal-1', 'Edit Job', {id: jobId}, '1000px')
      },
      handleEditProject(projectId) {
        this.$router.push({path: `/projects/${projectId}`})
      },
      handleShowPreferenceModal() {
        const payload = {
          title: 'Search Tasks',
          preferenceType: Constants.USER_PREFERENCE_TYPES.MY_TASKS
        }
        this.$eventHub.$emit('show-modal-1', 'Search and Sort Preferences', payload, '900px')
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      fetchByAssigneeAndStatus() {
        const payload = {
          taskStatus: this.$store.get('tasks/myTasks/selectedStatus'),
          userId: this.$store.get('security/userId')
        }

        if (payload.userId) {
          this.$store.dispatch('tasks/myTasks/fetchByAssigneeAndStatus', payload)
        }
      },
      async fetchByAssigneeAndStatusAndNotify() {
        await this.fetchByAssigneeAndStatus()
        notifySuccess(this, 'Tasks Refreshed Successfully')
      },
      isOrphanedTask(prop) {
        if (prop.row.orphaned) {
          return {backgroundColor: '#fbc658'}
        }
      }
    },
    async beforeMount() {
      this.timer = setInterval(this.fetchByAssigneeAndStatus, Constants.TIME.MILLIS.ONE_MINUTE * 2)
      await Promise.all([
        this.$store.cache.dispatch('statuses/fetchByEntityType', 'PtracTask'),
        this.$store.cache.dispatch('tasks/names/fetchAll'),
        this.$store.cache.dispatch('tasks/templates/fetchAll')
      ])
    },
    mounted() {
      this.taskStatus = Constants.STATUSES.PENDING
      this.$eventHub.$on('taskSaved', this.fetchByAssigneeAndStatus)
      this.$eventHub.$once('userAuthenticated', this.fetchByAssigneeAndStatus)
      this.filterByStatus()
    },
    beforeDestroy() {
      clearInterval(this.timer)
      this.$eventHub.$off('taskSaved')
      this.$eventHub.$off('userAuthenticated')
    },
    watch: {
      filteredTableColumns: function() {
        this.tableKey++
      },
      autoRefresh: {
        handler: function (autoRefresh) {
          if (autoRefresh) {
            setInterval(this.fetchByAssigneeAndStatus, Constants.TIME.MILLIS.ONE_MINUTE * 3)
          } else {
            clearInterval(this.timer)
          }
        }
      }
    }
  }
</script>

<style scoped>

  .my-tasks-filter {
    margin-top: -10px;
    padding-bottom: 8px;
  }

  #my-tasks-table .invisible {
    display: none;
  }
</style>
